import { useContext, useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { ieLeasingFormMapper } from '@/components/App/Leasing/helpers';
import type { TIeForm } from '@/components/App/Leasing/types';
import { debounce } from '@/services/helpers/debounce';
import { useAppSelector } from '@/store';

import { CreateTabContext } from '../../IeOrder';
import { getLeasingDraftKey } from '../../helpers';

const useIeLeasingForm = () => {
  const { request, mappedFiles, files } = useAppSelector(
    (state) => state.ieLeasingOrder
  );
  const { dicts } = useAppSelector((state) => state.dict);

  const { isFieldsFrozen } = useContext(CreateTabContext);

  const methods = useForm<TIeForm>({
    mode: isFieldsFrozen ? 'onSubmit' : 'all',
    defaultValues: ieLeasingFormMapper.toForm(),
    delayError: 200
  });

  const formData = useWatch({ control: methods.control });

  const saveDraftValuesToStorage = useMemo(
    () =>
      debounce((uuid: string, data: string) => {
        sessionStorage.setItem(getLeasingDraftKey(uuid), data);
      }, 500),
    []
  );

  // Сохранение данных в sessionStorage
  useEffect(() => {
    if (!request) return;

    if (request.status.code !== 'leasing_draft') {
      sessionStorage.removeItem(getLeasingDraftKey(request.uuid));
      return;
    }

    saveDraftValuesToStorage(request.uuid, JSON.stringify(formData));
  }, [formData, request?.uuid, request?.status]);

  // Заполнение формы
  useEffect(() => {
    if (!request || !dicts) return;

    const savedFormData = sessionStorage.getItem(
      getLeasingDraftKey(request.uuid)
    );

    if (savedFormData) {
      const parsedSavedData = JSON.parse(savedFormData);

      if (!parsedSavedData.clientPhoto?.base64) {
        parsedSavedData.clientPhoto = mappedFiles.clientPhoto ?? null;
      }
      if (!parsedSavedData.docPhoto?.base64) {
        parsedSavedData.docPhoto = mappedFiles.docPhoto ?? null;
      }

      if (
        !Array.isArray(parsedSavedData.subjects) ||
        parsedSavedData.subjects.length === 0
      ) {
        parsedSavedData.subjects = request.leasingSubjects.map(
          ieLeasingFormMapper.createIeSubject
        );
      }
      methods.reset(parsedSavedData);
      return;
    }

    methods.reset(ieLeasingFormMapper.toForm({ request, dicts, files }));
  }, [request, mappedFiles, files]);

  useEffect(() => {
    return () => {
      methods.reset(ieLeasingFormMapper.toForm());
    };
  }, []);

  return methods;
};

export default useIeLeasingForm;
